import { Box, Flex, Table, Text } from '@radix-ui/themes';
import type { CountryItem } from '@digiseg-labs/api-client';
import { useMemo, type ReactNode } from 'react';
import { DesktopIcon, EyeOpenIcon, HomeIcon, PersonIcon } from '@radix-ui/react-icons';
import { formatLargeNumber } from '../../utils/number';
import AudienceBuilderSankey from './AudienceBuilderSankey';
import type { ReachAnalysis, ReachMetric, SankeyLink, SankeyNode } from './types';

type Props = {
  analysis: ReachAnalysis;
  sankeyLinks: SankeyLink[];
  sankeyNodes?: SankeyNode[];
  selectedCountry: CountryItem | undefined;
  showEmptyState?: boolean;
  compact?: boolean;
  showSankey?: boolean;
};

type AnalysisRowProps = {
  label: string;
  icon: ReactNode;
  metric: ReachMetric;
  formatMetric: (value?: number) => string;
};

const AnalysisRow = ({ label, icon, metric, formatMetric }: AnalysisRowProps) => (
  <Table.Row>
    <Table.Cell>
      <Flex align="center" gap="2" className="builder-metric-label">
        <span className="builder-metric-icon" aria-hidden="true">{icon}</span>
        <span>{label}</span>
      </Flex>
    </Table.Cell>
    <Table.Cell>{formatMetric(metric.total)}</Table.Cell>
    <Table.Cell>{formatMetric(metric.segment)}</Table.Cell>
    <Table.Cell>{formatMetric(metric.inTarget)}</Table.Cell>
  </Table.Row>
);

const AudienceBuilderAnalysis = ({
  analysis,
  sankeyLinks,
  sankeyNodes,
  selectedCountry,
  showEmptyState = true,
  compact = false,
  showSankey = true,
}: Props) => {
  const locale = useMemo(() => (typeof navigator !== 'undefined' ? navigator.language : undefined), []);
  const formatMetric = (value?: number) => (
    value === undefined ? '-' : formatLargeNumber(value, locale)
  );
  const hasLinks = sankeyLinks.length > 0;

  return (
    <Box className={compact ? 'builder-analysis builder-analysis--compact' : 'builder-analysis'}>
      <Box className="builder-table-wrapper">
        <Table.Root className="builder-table">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>{selectedCountry?.display_name ?? 'Selected Country'}</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Total Market</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Segment Reach</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>In-Target Reach</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <AnalysisRow label="Households" icon={<HomeIcon />} metric={analysis.households} formatMetric={formatMetric} />
            <AnalysisRow label="Persons" icon={<PersonIcon />} metric={analysis.persons} formatMetric={formatMetric} />
            <AnalysisRow label="Devices" icon={<DesktopIcon />} metric={analysis.devices} formatMetric={formatMetric} />
            <AnalysisRow label="Imp. daily" icon={<EyeOpenIcon />} metric={analysis.impressions} formatMetric={formatMetric} />
          </Table.Body>
        </Table.Root>
      </Box>

      {showSankey && (hasLinks ? (
        <AudienceBuilderSankey links={sankeyLinks} nodes={sankeyNodes} />
      ) : showEmptyState ? (
        <Text size="2" color="gray" className="builder-empty">
          Select audiences to generate the Sankey diagram.
        </Text>
      ) : null)}
    </Box>
  );
};

export default AudienceBuilderAnalysis;
